// pages/AdminLogin.jsx - Optimized Dark Theme with Background Image
import React, { useState } from 'react';
import {
    Smartphone, Shield, Eye, EyeOff, Lock, Mail,
    ArrowRight, AlertCircle, CheckCircle2, Cloud,
    Check, Copy, Cpu, X, Key, Send, Users,
    MousePointerClick,
    LogIn
} from 'lucide-react';
import bgImage from '../assets/logoC.png';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE } from '../config/api'; // Import API_BASE and getHeaders

// Reusable Input Component
const InputField = ({ icon: Icon, type, value, onChange, placeholder, toggleShow, showPassword }) => (
    <div className="relative">
        <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#5a4f72' }} />
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full h-12 rounded-xl pl-12 pr-12 outline-none transition-all"
            style={{
                background: 'rgba(139,92,246,0.08)',
                border: '1px solid rgba(139,92,246,0.15)',
                color: '#e2d9f3',
            }}
            onFocus={e => {
                e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(139,92,246,0.1)';
            }}
            onBlur={e => {
                e.currentTarget.style.borderColor = 'rgba(139,92,246,0.15)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        />
        {toggleShow && (
            <button
                type="button"
                onClick={toggleShow}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: '#5a4f72' }}
                onMouseEnter={e => e.currentTarget.style.color = '#9c8fc0'}
                onMouseLeave={e => e.currentTarget.style.color = '#5a4f72'}
            >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        )}
    </div>
);

// Reusable Button Component
const Button = ({ children, loading, onClick, type = 'submit', fullWidth = true, variant = 'primary' }) => {
    const isPrimary = variant === 'primary';
    const baseStyle = fullWidth ? 'w-full' : '';
    const colorStyle = isPrimary
        ? { background: 'linear-gradient(135deg, #FF7D38, #FF6B1A)', boxShadow: '0 0 20px rgba(255,125,56,0.3)' }
        : { background: 'rgba(255,125,56,0.1)', color: '#FF7D38', border: '1px solid rgba(255,125,56,0.15)' };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={loading}
            className={`${baseStyle} h-12 rounded-xl text-white font-semibold transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isPrimary ? 'relative overflow-hidden group' : ''}`}
            style={colorStyle}
            onMouseEnter={e => {
                if (!e.currentTarget.disabled && isPrimary) {
                    e.currentTarget.style.boxShadow = '0 0 40px rgba(255,125,56,0.5)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                } else if (!isPrimary) {
                    e.currentTarget.style.background = 'rgba(255,125,56,0.2)';
                }
            }}
            onMouseLeave={e => {
                if (isPrimary) {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(255,125,56,0.3)';
                    e.currentTarget.style.transform = 'scale(1)';
                } else {
                    e.currentTarget.style.background = 'rgba(255,125,56,0.1)';
                }
            }}
        >
            {isPrimary && (
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: 'linear-gradient(135deg, rgba(255,125,56,0.3), rgba(255,107,26,0.3))' }}
                />
            )}
            {loading ? (
                <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Loading...</span>
                </>
            ) : children}
        </button>
    );
};

export const AdminLogin = ({ onLogin }) => {
    const [state, setState] = useState({
        email: '',
        password: '',
        showPw: false,
        loading: false,
        success: false
    });

    const [resetState, setResetState] = useState({
        email: '',
        otp: '',
        newPassword: '',
        confirmPassword: '',
        showNewPw: false,
        showConfirmPw: false,
        step: 1,
        loading: false,
        userId: ''
    });

    const [showForgotModal, setShowForgotModal] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const navigate = useNavigate();

    const updateState = (key, value) => setState(prev => ({ ...prev, [key]: value }));
    const updateResetState = (key, value) => setResetState(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { email, password } = state;

        if (!email || !password) {
            toast.error('Please fill in all fields.');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid email address.');
            return;
        }

        updateState('loading', true);
        updateState('success', false);

        try {
            const response = await axios.post(`${API_BASE}/api/super-admin/login`, {
                email: email,
                password: password
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                // Store token and admin data in sessionStorage
                sessionStorage.setItem('adminAuth', 'true');
                sessionStorage.setItem('adminToken', response.data.token);
                sessionStorage.setItem('adminData', JSON.stringify(response.data.superAdmin));

                updateState('success', true);
                updateState('loading', false);
                toast.success('Login successful! Welcome back, Admin!');

                // Redirect to dashboard
                setTimeout(() => {
                    navigate('/admin/dashboard');
                }, 500);                
            } else {
                updateState('loading', false);
                toast.error(response.data.message || 'Login failed. Please try again.');
            }
        } catch (error) {
            // error response for login api
            console.error('Login error:', error);

            if (error.response) {
                if (error.response.status === 401) {
                    toast.error('Invalid email or password. Please try again.');
                } else if (error.response.status === 404) {
                    toast.error('Server endpoint not found. Please check your connection.');
                } else {
                    toast.error(error.response.data.message || 'Login failed. Please try again.');
                }
            } else if (error.request) {
                toast.error('Unable to connect to server. Please check your network connection.');
            } else {
                toast.error('An unexpected error occurred. Please try again.');
            }

            updateState('loading', false);
        }
    };

    // Handle Generate OTP
    const handleSendOTP = async (e) => {
        e.preventDefault();
        const { email } = resetState;

        if (!email) {
            toast.error('Please enter your email address.');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error('Please enter a valid email address.');
            return;
        }

        updateResetState('loading', true);

        try {
            const response = await axios.post(
                `${API_BASE}/api/super-admin/generate-otp`,
                {
                    email: email
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                // Store userId from response
                updateResetState('userId', response.data.userInfo);
                updateResetState('step', 2);
                updateResetState('loading', false);
                toast.success(response.data.message || 'OTP sent successfully to your email!');
            } else {
                updateResetState('loading', false);
                toast.error(response.data.message || 'Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);

            if (error.response) {
                if (error.response.status === 404) {
                    toast.error('Email not found. Please check your email address.');
                } else {
                    toast.error(error.response.data.message || 'Failed to send OTP. Please try again.');
                }
            } else if (error.request) {
                toast.error('Unable to connect to server. Please check your network connection.');
            } else {
                toast.error('An unexpected error occurred. Please try again.');
            }

            updateResetState('loading', false);
        }
    };

    // Handle Compare OTP
    const handleCompareOTP = async (e) => {
        e.preventDefault();
        const { otp, userId } = resetState;

        if (!otp) {
            toast.error('Please enter the OTP code.');
            return;
        }

        if (otp.length !== 6) {
            toast.error('Please enter a valid 6-digit OTP.');
            return;
        }

        if (!userId) {
            toast.error('User ID not found. Please try generating OTP again.');
            return;
        }

        updateResetState('loading', true);

        try {
            const response = await axios.post(
                `${API_BASE}/api/super-admin/compare-otp`,
                {
                    _id: userId,
                    emailOtp: otp
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                updateResetState('step', 3); // Move to password reset step
                updateResetState('loading', false);
                toast.success(response.data.message || 'OTP verified successfully!');
            } else {
                updateResetState('loading', false);
                toast.error(response.data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);

            if (error.response) {
                toast.error(error.response.data.message || 'Invalid OTP. Please try again.');
            } else if (error.request) {
                toast.error('Unable to connect to server. Please check your network connection.');
            } else {
                toast.error('An unexpected error occurred. Please try again.');
            }

            updateResetState('loading', false);
        }
    };

    // Handle Reset Password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        const { newPassword, confirmPassword, userId } = resetState;

        if (!newPassword || !confirmPassword) {
            toast.error('Please fill in all fields.');
            return;
        }

        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters.');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }

        if (!userId) {
            toast.error('User ID not found. Please try again.');
            return;
        }

        updateResetState('loading', true);

        try {
            const response = await axios.put(
                `${API_BASE}/api/super-admin/reset-password`,
                {
                    userId: userId,
                    newpassword: newPassword,
                    confirmpassword: confirmPassword
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                updateResetState('loading', false);
                toast.success(response.data.message || 'Password reset successful!');

                // Close modal after success
                setTimeout(() => {
                    setShowForgotModal(false);
                    setResetState({
                        email: '', otp: '', newPassword: '', confirmPassword: '',
                        showNewPw: false, showConfirmPw: false, step: 1,
                        loading: false, userId: ''
                    });
                }, 2000);
            } else {
                updateResetState('loading', false);
                toast.error(response.data.message || 'Failed to reset password. Please try again.');
            }
        } catch (error) {
            console.error('Error resetting password:', error);

            if (error.response) {
                toast.error(error.response.data.message || 'Failed to reset password. Please try again.');
            } else if (error.request) {
                toast.error('Unable to connect to server. Please check your network connection.');
            } else {
                toast.error('An unexpected error occurred. Please try again.');
            }

            updateResetState('loading', false);
        }
    };

    const closeModal = () => {
        setShowForgotModal(false);
        setResetState({
            email: '', otp: '', newPassword: '', confirmPassword: '',
            showNewPw: false, showConfirmPw: false, step: 1,
            loading: false, userId: ''
        });
    };

    const STAFF_LOGIN_URL = `${window.location.origin}/staff-login`;

    const copyStaffLoginLink = async () => {
        try {
            await navigator.clipboard.writeText(STAFF_LOGIN_URL);
            setCopySuccess(true);
            toast.success('Staff login link copied to clipboard!');
            setTimeout(() => setCopySuccess(false), 3000);
        } catch {
            const textArea = document.createElement("textarea");
            textArea.value = STAFF_LOGIN_URL;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setCopySuccess(true);
            toast.success('Staff login link copied to clipboard!');
            setTimeout(() => setCopySuccess(false), 3000);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #02203C, #02203C)', boxShadow: '0 0 20px rgba(2,32,60,0.3)' }}>
            {/* Background */}
            <div className="absolute inset-0 z-0">
                {/* <img src={bgImage} alt="Background" className="w-full h-full object-cover" /> */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(2,32,60,0.92), rgba(2,32,60,0.88), rgba(2,32,60,0.95))' }} />
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,125,56,0.08), transparent 70%)' }} />
            </div>

            {/* Main Container */}
            <div className="relative z-10 w-full max-w-6xl rounded-3xl shadow-2xl overflow-hidden" style={{ border: '1px solid rgba(255,125,56,0.2)', backdropFilter: 'blur(10px)' }}>
                <div className="flex flex-col lg:flex-row">
                    {/* Left Side */}
                    <div className="relative lg:w-1/2 h-64 sm:h-72 md:h-80 lg:h-[600px] overflow-hidden">
                        <div className="absolute inset-0">
                            <img src={bgImage} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 overflow-hidden">
                            {[...Array(30)].map((_, i) => (
                                <div key={i} className="absolute rounded-full animate-pulse" style={{
                                    width: Math.random() * 4 + 2 + 'px',
                                    height: Math.random() * 4 + 2 + 'px',
                                    background: 'rgba(255,125,56,0.15)',
                                    left: Math.random() * 100 + '%',
                                    top: Math.random() * 100 + '%',
                                    animationDuration: Math.random() * 4 + 2 + 's',
                                    animationDelay: Math.random() * 3 + 's',
                                }} />
                            ))}
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12">
                        <div className="w-full max-w-md">
                            <div className="mb-6 lg:mb-8">
                                <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#e2d9f3' }}>Mobilink Login</h2>
                                <p className="text-sm sm:text-base mt-1" style={{ color: '#5a4f72' }}>Access your Super Admin Dashboard</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Admin Email Address</label>
                                    <InputField
                                        icon={Mail}
                                        type="email"
                                        placeholder="Enter your admin email"
                                        value={state.email}
                                        onChange={e => updateState('email', e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Password</label>
                                    <InputField
                                        icon={Lock}
                                        type={state.showPw ? "text" : "password"}
                                        value={state.password}
                                        placeholder="Enter your password"
                                        onChange={e => updateState('password', e.target.value)}
                                        toggleShow={() => updateState('showPw', !state.showPw)}
                                        showPassword={state.showPw}
                                    />
                                </div>

                                <div className="flex justify-end">
                                    <button type="button" onClick={() => setShowForgotModal(true)} className="text-xs font-medium transition-colors" style={{ color: '#FF7D38' }}
                                        onMouseEnter={e => e.currentTarget.style.color = '#FF9A5F'}
                                        onMouseLeave={e => e.currentTarget.style.color = '#FF7D38'}>
                                        Forgot password?
                                    </button>
                                </div>

                                <Button loading={state.loading}>
                                    Sign In <LogIn size={18} className="group-hover:translate-x-1 transition-transform" />
                                </Button>

                                {/* Staff Portal */}
                                <div
                                    className="mt-8 pt-6 border-t"
                                    style={{ borderColor: "#2b2540" }}
                                >
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">

                                        {/* Left */}
                                        <div className="flex items-center gap-3 text-center md:text-left">
                                            <div
                                                className="w-11 h-11 rounded-xl flex items-center justify-center"
                                                style={{ background: "rgb(255, 255, 255)" }}
                                            >
                                                <Users size={20} style={{ color: "#FF7D38" }} />
                                            </div>

                                            <div>
                                                <p
                                                    className="text-sm font-semibold"
                                                    style={{ color: "#e2d9f3" }}
                                                >
                                                    Staff Portal
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right */}
                                        <div className="flex w-full md:w-auto gap-2">
                                            <a
                                                href="/staff-login"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 md:flex-none flex items-center justify-center gap-2 text-center px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                                                style={{
                                                    background: "#FF7D38",
                                                    color: "#fff",
                                                }}
                                            >
                                                <LogIn size={16} />
                                                Staff Login
                                            </a>

                                            <button
                                                type="button"
                                                onClick={copyStaffLoginLink}
                                                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                                                style={{
                                                    background: copySuccess ? "#34d399" : "#1F2937",
                                                    color: "#fff",
                                                }}
                                            >
                                                {copySuccess ? (
                                                    <>
                                                        <Check size={16} />
                                                        Copied
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={16} />
                                                        Copy Link
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0" style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }} onClick={closeModal} />
                    <div className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'rgba(20, 16, 36, 0.95)', border: '1px solid rgba(255,125,56,0.2)', backdropFilter: 'blur(20px)' }}>
                        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,125,56,0.15)' }}>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,125,56,0.15)' }}>
                                    <Key size={16} style={{ color: '#FF7D38' }} />
                                </div>
                                <h3 className="text-lg font-semibold" style={{ color: '#e2d9f3' }}>Reset Password</h3>
                            </div>
                            <button onClick={closeModal} className="p-1 rounded-lg transition-colors" style={{ color: '#5a4f72' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#9c8fc0'}
                                onMouseLeave={e => e.currentTarget.style.color = '#5a4f72'}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="px-6 py-6">
                            {/* Step 1: Enter Email */}
                            {resetState.step === 1 && (
                                <form onSubmit={handleSendOTP} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Email Address</label>
                                        <InputField
                                            icon={Mail}
                                            type="email"
                                            value={resetState.email}
                                            onChange={e => updateResetState('email', e.target.value)}
                                            placeholder="Enter your registered email"
                                        />
                                        <p className="text-xs mt-2" style={{ color: '#5a4f72' }}>We'll send a verification code to this email</p>
                                    </div>
                                    <Button loading={resetState.loading}><Send size={18} /> Send OTP</Button>
                                </form>
                            )}

                            {/* Step 2: Verify OTP */}
                            {resetState.step === 2 && (
                                <form onSubmit={handleCompareOTP} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>OTP Code</label>
                                        <InputField
                                            icon={Shield}
                                            type="text"
                                            value={resetState.otp}
                                            onChange={e => updateResetState('otp', e.target.value.slice(0, 6))}
                                            placeholder="Enter 6-digit OTP"
                                        />
                                        <p className="text-xs mt-2" style={{ color: '#5a4f72' }}>Enter the verification code sent to your email</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <Button type="button" variant="secondary" onClick={() => {
                                            updateResetState('step', 1);
                                            updateResetState('otp', '');
                                        }}>Back</Button>
                                        <Button loading={resetState.loading}><Shield size={18} /> Verify OTP</Button>
                                    </div>
                                </form>
                            )}

                            {/* Step 3: Reset Password */}
                            {resetState.step === 3 && (
                                <form onSubmit={handleResetPassword} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>New Password</label>
                                        <InputField
                                            icon={Lock}
                                            type={resetState.showNewPw ? "text" : "password"}
                                            value={resetState.newPassword}
                                            onChange={e => updateResetState('newPassword', e.target.value)}
                                            placeholder="Enter new password (min 6 chars)"
                                            toggleShow={() => updateResetState('showNewPw', !resetState.showNewPw)}
                                            showPassword={resetState.showNewPw}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#9c8fc0' }}>Confirm Password</label>
                                        <InputField
                                            icon={Lock}
                                            type={resetState.showConfirmPw ? "text" : "password"}
                                            value={resetState.confirmPassword}
                                            onChange={e => updateResetState('confirmPassword', e.target.value)}
                                            placeholder="Confirm new password"
                                            toggleShow={() => updateResetState('showConfirmPw', !resetState.showConfirmPw)}
                                            showPassword={resetState.showConfirmPw}
                                        />
                                    </div>
                                    <div className="flex gap-3">
                                        <Button type="button" variant="secondary" onClick={() => {
                                            updateResetState('step', 2);
                                            updateResetState('newPassword', '');
                                            updateResetState('confirmPassword', '');
                                        }}>Back</Button>
                                        <Button loading={resetState.loading}><Key size={18} /> Reset Password</Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};